import React, { useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import authService from '../services/auth.service';
import NotificationSnoozeSettings from '../components/NotificationSnoozeSettings';
import { toast } from 'react-toastify';

const Profile: React.FC = () => {
  const { user } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const hasPassword = user?.provider == null || user?.provider === '';

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!password.trim()) {
      toast.error('New password is required');
      return;
    }
    if (password.length < 8) {
      toast.error('New password must be at least 8 characters');
      return;
    }
    if (password !== passwordConfirmation) {
      toast.error('New password and confirmation do not match');
      return;
    }
    if (hasPassword && !currentPassword.trim()) {
      toast.error('Current password is required');
      return;
    }
    setSubmitting(true);
    try {
      const payload: {
        current_password?: string;
        password: string;
        password_confirmation: string;
      } = {
        password,
        password_confirmation: passwordConfirmation,
      };
      if (hasPassword) {
        payload.current_password = currentPassword;
      }
      await authService.changePassword(payload);
      toast.success('Password updated successfully.');
      setCurrentPassword('');
      setPassword('');
      setPasswordConfirmation('');
    } catch (err: any) {
      const msg =
        err.response?.data?.errors?.current_password?.[0] ||
        err.response?.data?.errors?.password?.[0] ||
        err.response?.data?.message ||
        'Failed to update password.';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className='row'>
      <div className='col-12'>
        <h1 className='page-title mb-4'>Profile</h1>
      </div>

      <div className='col-lg-6'>
        <div className='card'>
          <div className='card-header'>
            <h3 className='card-title mb-0'>Account</h3>
          </div>
          <div className='card-body'>
            <div className='mb-3'>
              <label className='form-label text-muted'>Name</label>
              <p className='mb-0 fw-medium'>{user?.name || '—'}</p>
            </div>
            <div className='mb-3'>
              <label className='form-label text-muted'>Email</label>
              <p className='mb-0 fw-medium'>{user?.email || '—'}</p>
            </div>
            {user?.firm && (
              <div className='mb-0'>
                <label className='form-label text-muted'>Firm</label>
                <p className='mb-0 fw-medium'>{user.firm.name}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className='col-lg-6'>
        <div className='card'>
          <div className='card-header'>
            <h3 className='card-title mb-0'>Change password</h3>
          </div>
          <div className='card-body'>
            <form onSubmit={handleChangePassword}>
              {hasPassword && (
                <div className='mb-3'>
                  <label htmlFor='current-password' className='form-label'>
                    Current password
                  </label>
                  <input
                    id='current-password'
                    type='password'
                    className='form-control'
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    autoComplete='current-password'
                  />
                </div>
              )}
              <div className='mb-3'>
                <label htmlFor='new-password' className='form-label'>
                  New password
                </label>
                <input
                  id='new-password'
                  type='password'
                  className='form-control'
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete='new-password'
                  minLength={8}
                />
                <div className='form-text'>At least 8 characters.</div>
              </div>
              <div className='mb-4'>
                <label htmlFor='confirm-password' className='form-label'>
                  Confirm new password
                </label>
                <input
                  id='confirm-password'
                  type='password'
                  className='form-control'
                  value={passwordConfirmation}
                  onChange={(e) => setPasswordConfirmation(e.target.value)}
                  autoComplete='new-password'
                />
              </div>
              <button
                type='submit'
                className='btn btn-primary'
                disabled={submitting}
              >
                {submitting ? 'Updating…' : 'Update password'}
              </button>
            </form>
          </div>
        </div>
      </div>

      <div className='col-12 mt-4'>
        <div className='card'>
          <div className='card-header'>
            <h3 className='card-title mb-0'>Email notification snooze</h3>
          </div>
          <div className='card-body'>
            <NotificationSnoozeSettings />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
